import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import CourseVideoPlayer from './CourseVideoPlayer';
import { ChevronDownIcon, CheckCircleIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../Common/LoadingSpinner';
import toast from 'react-hot-toast';

function CourseLearn() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expandedModules, setExpandedModules] = useState([]);
    const [completedLectures, setCompletedLectures] = useState([]);
    const [completedModules, setCompletedModules] = useState([]);
    const [currentVideo, setCurrentVideo] = useState(null);
    const [hasStarted, setHasStarted] = useState(false);

    useEffect(() => {
        fetchCourseData();
    }, [id]);

    useEffect(() => {
        // Check module completion whenever completed lectures change
        if (course && course.modules) {
            course.modules.forEach((_, moduleIndex) => {
                checkModuleCompletion(moduleIndex);
            });
        }
    }, [completedLectures, course]);

    const fetchCourseData = async () => {
        try {
            const response = await axios.get(`/api/courses/${id}`);
            setCourse(response.data);

            // Fetch progress data
            try {
                const progressResponse = await axios.get(`/api/progress/course/${id}`);
                const progressData = progressResponse.data.progress;

                // Set completed lectures from progress data
                if (progressData && progressData.modulesProgress && Array.isArray(progressData.modulesProgress)) {
                    const completedLectureIds = [];
                    const completedModuleIndices = [];

                    progressData.modulesProgress.forEach((moduleProgress, moduleIndex) => {
                        if (moduleProgress.completed) {
                            completedModuleIndices.push(moduleIndex);
                        }
                        if (moduleProgress.lectures && Array.isArray(moduleProgress.lectures)) {
                            moduleProgress.lectures.forEach((lecture, lectureIndex) => {
                                if (lecture.completed) {
                                    completedLectureIds.push(`${moduleIndex}-${lectureIndex}`);
                                }
                            });
                        }
                    });

                    setCompletedLectures(completedLectureIds);
                    setCompletedModules(completedModuleIndices);
                }
            } catch (progressError) {
                console.log('Progress not found, starting fresh');
                // Progress might not exist yet, that's okay
            }

            // Expand first module by default
            if (response.data.modules && response.data.modules.length > 0) {
                setExpandedModules([0]);
                // Set first lecture as current video if available
                if (response.data.modules[0].lectures && response.data.modules[0].lectures.length > 0) {
                    setCurrentVideo({
                        title: response.data.modules[0].lectures[0].title,
                        url: response.data.modules[0].lectures[0].videoUrl,
                        moduleIndex: 0,
                        lectureIndex: 0,
                        lectureId: response.data.modules[0].lectures[0]._id
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching course:', error);
            toast.error('Failed to load course');
        } finally {
            setLoading(false);
        }
    };

    const toggleModule = (index) => {
        setExpandedModules(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    const markLectureAsDone = async (moduleIndex, lectureIndex) => {
        const lectureId = `${moduleIndex}-${lectureIndex}`;
        const lecture = course.modules[moduleIndex].lectures[lectureIndex];
        const moduleId = course.modules[moduleIndex]._id;
        const wasCompleted = completedLectures.includes(lectureId);

        try {
            // Toggle lecture completion via API
            const response = await axios.post(`/api/progress/lecture/${id}/${moduleId}/${lecture._id}/toggle`);

            console.log('Lecture toggle response:', response.data.message);

            // Update local state
            setCompletedLectures(prev =>
                prev.includes(lectureId)
                    ? prev.filter(id => id !== lectureId)
                    : [...prev, lectureId]
            );

            toast.success(
                wasCompleted
                    ? 'Lecture unmarked'
                    : 'Lecture marked as complete'
            );
        } catch (error) {
            console.error('Error toggling lecture:', error);
            toast.error('Failed to update lecture status');
        }
    };

    const markModuleAsDone = async (moduleIndex) => {
        const moduleId = course.modules[moduleIndex]._id;
        const lectures = course.modules[moduleIndex].lectures;

        try {
            // Mark all lectures in the module as complete via API (only if not already complete)
            for (let lectureIndex = 0; lectureIndex < lectures.length; lectureIndex++) {
                const lectureId = `${moduleIndex}-${lectureIndex}`;
                if (!completedLectures.includes(lectureId)) {
                    await axios.post(`/api/progress/lecture/${id}/${moduleId}/${lectures[lectureIndex]._id}/toggle`);
                }
            }

            // Update local state
            setCompletedModules(prev =>
                prev.includes(moduleIndex)
                    ? prev.filter(i => i !== moduleIndex)
                    : [...prev, moduleIndex]
            );

            // Also mark all lectures in the module as complete in local state
            const lecturesToComplete = lectures.map((_, idx) =>
                `${moduleIndex}-${idx}`
            );
            setCompletedLectures(prev => {
                const newCompleted = [...prev];
                lecturesToComplete.forEach(lectureId => {
                    if (!newCompleted.includes(lectureId)) {
                        newCompleted.push(lectureId);
                    }
                });
                return newCompleted;
            });

            toast.success('Module marked as complete');
        } catch (error) {
            console.error('Error marking module as complete:', error);
            toast.error('Failed to mark module as complete');
        }
    };

    const checkModuleCompletion = (moduleIndex) => {
        const module = course.modules[moduleIndex];
        if (!module || !module.lectures) return;

        const allLecturesComplete = module.lectures.every((_, lectureIndex) =>
            completedLectures.includes(`${moduleIndex}-${lectureIndex}`)
        );

        if (allLecturesComplete && !completedModules.includes(moduleIndex)) {
            setCompletedModules(prev => [...prev, moduleIndex]);
            toast.success(`Module ${moduleIndex + 1} completed!`);
        }
    };

    const selectLecture = (lecture, moduleIndex, lectureIndex) => {
        setCurrentVideo({
            title: lecture.title,
            url: lecture.videoUrl,
            moduleIndex,
            lectureIndex,
            lectureId: lecture._id
        });
        setHasStarted(true);
    };

    const handleVideoEnd = async () => {
        // Mark current lecture as complete
        if (currentVideo) {
            const lectureId = `${currentVideo.moduleIndex}-${currentVideo.lectureIndex}`;

            if (!completedLectures.includes(lectureId)) {
                await markLectureAsDone(currentVideo.moduleIndex, currentVideo.lectureIndex);
            }

            // Move to next lecture
            playNextLecture();
        }
    };

    const playNextLecture = () => {
        if (!course || !currentVideo) return;

        const currentModule = course.modules[currentVideo.moduleIndex];
        const nextLectureIndex = currentVideo.lectureIndex + 1;

        // Check if there's a next lecture in current module
        if (nextLectureIndex < currentModule.lectures.length) {
            const nextLecture = currentModule.lectures[nextLectureIndex];
            setCurrentVideo({
                title: nextLecture.title,
                url: nextLecture.videoUrl,
                moduleIndex: currentVideo.moduleIndex,
                lectureIndex: nextLectureIndex,
                lectureId: nextLecture._id
            });
            setHasStarted(true);
        } else {
            // Move to next module
            const nextModuleIndex = currentVideo.moduleIndex + 1;
            if (nextModuleIndex < course.modules.length) {
                const nextModule = course.modules[nextModuleIndex];
                if (nextModule.lectures && nextModule.lectures.length > 0) {
                    setCurrentVideo({
                        title: nextModule.lectures[0].title,
                        url: nextModule.lectures[0].videoUrl,
                        moduleIndex: nextModuleIndex,
                        lectureIndex: 0,
                        lectureId: nextModule.lectures[0]._id
                    });
                    setHasStarted(true);
                    // Expand next module
                    setExpandedModules(prev => [...prev, nextModuleIndex]);
                    toast.success(`Moving to Module ${nextModuleIndex + 1}`);
                } else {
                    toast.success('Course completed! 🎉');
                }
            } else {
                toast.success('Course completed! 🎉');
            }
        }
    };

    const isLectureCompleted = (moduleIndex, lectureIndex) => {
        return completedLectures.includes(`${moduleIndex}-${lectureIndex}`);
    };

    const isModuleCompleted = (moduleIndex) => {
        return completedModules.includes(moduleIndex);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <LoadingSpinner />
            </div>
        );
    }

    if (!course) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Course not found</h2>
                    <p className="text-gray-600">The course you're looking for doesn't exist.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-2">
                <div className="flex items-center space-x-4">
                    <button
                        onClick={() => navigate(`/courses/${id}`)}
                        className="text-gray-600 hover:text-gray-900 transition-colors"
                    >
                        <ArrowLeftIcon className="h-6 w-6" />
                    </button>
                    <div className='flex gap-2'>
                        <h1 className="text-2xl font-bold text-gray-900">{course.title}</h1>
                        <p className="text-gray-600 mt-1">({course.courseCode})</p>
                    </div>
                </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4">
                {/* Video Player - 2/3 width */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-lg shadow-sm p-4">
                        {/* <h2 className="text-xl font-semibold text-gray-900 mb-4">
                            {currentVideo?.title || 'Select a lecture to begin'}
                        </h2> */}
                        <CourseVideoPlayer
                            videoUrl={currentVideo?.url}
                            title={currentVideo?.title}
                            thumbnail={course?.thumbnail}
                            hasStarted={hasStarted}
                            onVideoEnd={handleVideoEnd}
                        />
                        {currentVideo && (
                            <div className="mt-4">
                                <button
                                    onClick={() => markLectureAsDone(currentVideo.moduleIndex, currentVideo.lectureIndex)}
                                    className={`btn ${isLectureCompleted(currentVideo.moduleIndex, currentVideo.lectureIndex)
                                        ? 'bg-green-600 hover:bg-green-700'
                                        : 'btn-primary'
                                        } flex items-center space-x-2`}
                                >
                                    <CheckCircleIcon className="h-5 w-5" />
                                    <span>
                                        {isLectureCompleted(currentVideo.moduleIndex, currentVideo.lectureIndex)
                                            ? 'Completed'
                                            : 'Mark as Done'
                                        }
                                    </span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Course Modules - 1/3 width */}
                <div className="lg:col-span-1 ">
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
                        <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
                            <h3 className="font-semibold text-lg text-gray-900">Course Content</h3>
                        </div>

                        <div className="hide-scrollbar overflow-y-auto" style={{ maxHeight: 'calc(100vh - 250px)' }}>
                            {course.modules && course.modules.length > 0 ? (
                                <div className="divide-y divide-gray-200">
                                    {course.modules.map((module, moduleIndex) => (
                                        <div key={moduleIndex} className="border-b border-gray-100 last:border-b-0">
                                            {/* Module Header */}
                                            <div className="bg-gradient-to-r from-slate-50 to-gray-50">
                                                <button
                                                    onClick={() => toggleModule(moduleIndex)}
                                                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-gradient-to-r hover:from-slate-100 hover:to-gray-100 transition-all duration-200"
                                                >
                                                    <div className="flex items-center space-x-3 flex-1 text-left">
                                                        <span className={`text-sm font-semibold ${isModuleCompleted(moduleIndex) ? 'text-green-600' : 'text-gray-900'
                                                            }`}>
                                                            <span className='font-bold'>Module {moduleIndex + 1}:</span> {module.title}
                                                        </span>
                                                    </div>
                                                    <div className={`transition-transform duration-200 ${expandedModules.includes(moduleIndex) ? 'rotate-180' : ''}`}>
                                                        <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                                                    </div>
                                                </button>

                                                {/* Module Mark as Done Button */}
                                                <div className="px-6 pb-4">
                                                    <button
                                                        onClick={() => markModuleAsDone(moduleIndex)}
                                                        className={`w-full px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center space-x-2 shadow-sm hover:shadow-md ${isModuleCompleted(moduleIndex)
                                                            ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700'
                                                            : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700'
                                                            }`}
                                                    >
                                                        <CheckCircleIcon className="h-5 w-5" />
                                                        <span>
                                                            {isModuleCompleted(moduleIndex) ? 'Module Completed' : 'Mark Module as Done'}
                                                        </span>
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Lectures */}
                                            {expandedModules.includes(moduleIndex) && module.lectures && (
                                                <div className="bg-white">
                                                    {module.lectures.map((lecture, lectureIndex) => {
                                                        const isCompleted = isLectureCompleted(moduleIndex, lectureIndex);
                                                        const isCurrent = currentVideo?.moduleIndex === moduleIndex &&
                                                            currentVideo?.lectureIndex === lectureIndex;

                                                        return (
                                                            <div key={lectureIndex} className="border-t border-gray-100 first:border-t-0">
                                                                <div className={`px-6 py-4 ${isCurrent ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'hover:bg-gray-50'} transition-all duration-200`}>
                                                                    <div className="flex items-start justify-between gap-4">
                                                                        <button
                                                                            onClick={() => selectLecture(lecture, moduleIndex, lectureIndex)}
                                                                            className="flex-1 text-left"
                                                                        >
                                                                            <div className="flex items-start space-x-3">
                                                                                <div className={`mt-0.5 ${isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
                                                                                    <CheckCircleIcon className="h-5 w-5" />
                                                                                </div>
                                                                                <div className="flex-1">
                                                                                    <p className={`text-sm font-medium ${isCurrent ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-900'
                                                                                        }`}>
                                                                                        {lecture.title}
                                                                                    </p>
                                                                                    {lecture.duration && (
                                                                                        <p className="text-xs text-gray-500 mt-1 flex items-center">
                                                                                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                                            </svg>
                                                                                            {lecture.duration} min
                                                                                        </p>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </button>

                                                                        <button
                                                                            onClick={() => markLectureAsDone(moduleIndex, lectureIndex)}
                                                                            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 whitespace-nowrap shadow-sm hover:shadow ${isCompleted
                                                                                ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700'
                                                                                : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 hover:from-gray-200 hover:to-gray-300 border border-gray-300'
                                                                                }`}
                                                                        >
                                                                            {isCompleted ? '✓ Done' : 'Mark Done'}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-8 text-center">
                                    <p className="text-gray-500">No modules available yet</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CourseLearn;
